import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SplashCursor from '../components/SplashCursor';
import { FaDatabase, FaBrain, FaPuzzlePiece, FaCode } from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: transparent;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
  scroll-behavior: smooth;
  overflow-y: auto;

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.3);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 5px;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
  }
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 50;
  padding: 0 5%;
  pointer-events: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.8s ease-out;
  scroll-behavior: smooth;
`;

const HeroSection = styled.div`
  padding-top: 100px;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto 2rem;

  @media (max-width: 768px) {
    padding-top: 80px;
    margin: 0 auto 1.5rem;
  }
`;

const Title = styled.h1`
  font-size: 3.75rem;
  margin-bottom: 1rem;
  color: white;
  line-height: 1.2;
  font-weight: bold;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.79);

  @media (max-width: 768px) {
    font-size: 2.75rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 2.25rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  padding: 0 1rem;
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin: 4rem auto;
  max-width: 800px;

  @media (max-width: 768px) {
    margin: 3rem auto;
    padding: 0 1rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  background: white;
  color: #000;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.9rem 1.8rem;
    font-size: 1.1rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(255, 255, 255, 0.3);
  }
`;

const ArchitectureSection = styled.div`
  background: transparent;
  padding: 60px 40px;
  border-radius: 10px;
  margin-bottom: 120px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 40px 20px;
    margin-bottom: 80px;
  }
`;

const ArchitectureTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 60px;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 40px;
  }
`;

const ArchitectureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  text-align: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ArchitectureIcon = styled.div`
  font-size: 3rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }
`;

const ArchitectureItemTitle = styled.h4`
  color: white;
  margin-bottom: 15px;
  font-size: 1.2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 10px;
  }
`;

const ArchitectureItemDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  line-height: 1.5;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const ArchitectureItem = styled.div`
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    
    ${ArchitectureIcon} {
  color: white;
      transform: translateY(-2px);
    }
  }
`;

const GeoOS = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <SplashCursor />
      <Navbar />
      <ContentContainer>
        <HeroSection>
          <Title>
            Geo OS
          </Title>
          <Subtitle>
            A modular and scalable Open Ecosystem optimized for AI-powered Autonomous Operations.
          </Subtitle>
        </HeroSection>

        <div style={{ 
          maxWidth: "1200px", 
          margin: "40px auto", 
          padding: "0 20px"
        }}>
          {/* System Architecture - Now First */}
          <ArchitectureSection>
            <ArchitectureTitle>
              System Architecture
            </ArchitectureTitle>
            <ArchitectureGrid>
              <ArchitectureItem>
                <ArchitectureIcon>
                  <FaDatabase />
                </ArchitectureIcon>
                <ArchitectureItemTitle>Data Layer</ArchitectureItemTitle>
                <ArchitectureItemDescription>
                  Unified data storage and processing infrastructure
                </ArchitectureItemDescription>
              </ArchitectureItem>
              <ArchitectureItem>
                <ArchitectureIcon>
                  <FaBrain />
                </ArchitectureIcon>
                <ArchitectureItemTitle>AI Layer</ArchitectureItemTitle>
                <ArchitectureItemDescription>
                  Advanced machine learning and decision systems
                </ArchitectureItemDescription>
              </ArchitectureItem>
              <ArchitectureItem>
                <ArchitectureIcon>
                  <FaPuzzlePiece />
                </ArchitectureIcon>
                <ArchitectureItemTitle>Function Layer</ArchitectureItemTitle>
                <ArchitectureItemDescription>
                  Modular Data Modeling and Optimization Algorithms
                </ArchitectureItemDescription>
              </ArchitectureItem>
              <ArchitectureItem>
                <ArchitectureIcon>
                  <FaCode />
                </ArchitectureIcon>
                <ArchitectureItemTitle>API Layer</ArchitectureItemTitle>
                <ArchitectureItemDescription>
                  Developer tools and interfaces
                </ArchitectureItemDescription>
              </ArchitectureItem>
            </ArchitectureGrid>
          </ArchitectureSection>

          {/* Geo OS Data Section */}
          <div style={{ 
            display: "flex", 
            alignItems: "center",
            marginBottom: "120px",
            gap: "80px"
          }}>
            {/* Data Visualization */}
            <div style={{ flex: "0 0 40%" }}>
              <svg width="100%" height="300" viewBox="0 0 400 300">
                <defs>
                  <linearGradient id="dataGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#111" />
                    <stop offset="100%" stopColor="#333" />
                  </linearGradient>
                </defs>
                {/* 3D Data Cube */}
                <g transform="translate(200, 150)">
                  {/* Cube faces */}
                  <path d="M-60,-60 L60,-60 L60,60 L-60,60 Z" fill="url(#dataGradient)" stroke="white" strokeWidth="1.5" />
                  <path d="M60,-60 L100,-20 L100,100 L60,60 Z" fill="url(#dataGradient)" stroke="white" strokeWidth="1.5" />
                  <path d="M-60,60 L60,60 L100,100 L-20,100 Z" fill="url(#dataGradient)" stroke="white" strokeWidth="1.5" />
                  
                  {/* Glowing data elements */}
                  <rect x="-40" y="-40" width="30" height="30" fill="#0066FF" fillOpacity="0.7" stroke="white" strokeWidth="1" />
                  <rect x="20" y="-20" width="20" height="20" fill="#0066FF" fillOpacity="0.7" stroke="white" strokeWidth="1" />
                  <rect x="-30" y="30" width="40" height="15" fill="#0066FF" fillOpacity="0.7" stroke="white" strokeWidth="1" />
                  
                  {/* Data connections */}
                  <line x1="-25" y1="-25" x2="30" y2="-10" stroke="#0066FF" strokeWidth="1.5" />
                  <line x1="30" y1="-10" x2="-10" y2="30" stroke="#0066FF" strokeWidth="1.5" />
                </g>
              </svg>
            </div>

            {/* Content */}
            <div style={{ flex: "1" }}>
              <div style={{ 
                borderLeft: "4px solid white",
                paddingLeft: "30px" 
              }}>
                <h3 style={{ 
                  fontSize: "2rem", 
                  fontWeight: "bold", 
                  color: "white", 
                  marginBottom: "20px" 
                }}>
                  [Geo OS Data]
                </h3>
                <p style={{ 
                  fontSize: "1.2rem", 
                  color: "rgba(255, 255, 255, 0.9)", 
                  lineHeight: "1.6", 
                  marginBottom: "30px" 
                }}>
                  Seamlessly integrates and harmonizes data across land, sea, air, and space to provide a comprehensive all-domain data engine.
                </p>
                <ul style={{ 
                  listStyle: "none", 
                  padding: 0, 
                  margin: 0
                }}>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Universal Data Protocol
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      A unified language for all geospatial data types ensures seamless communication between systems.
                    </p>
                  </li>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Robust Data Fusion
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      Proprietary techniques for combining structured and unstructured data sources such as static charts, satellite imagery, weather data, and real-time observations.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Geo OS Model Section - Reversed */}
          <div style={{ 
            display: "flex", 
            flexDirection: "row-reverse",
            alignItems: "center",
            marginBottom: "120px",
            gap: "80px"
          }}>
            {/* Neural Network Visualization */}
            <div style={{ flex: "0 0 40%" }}>
              <svg width="100%" height="300" viewBox="0 0 400 300">
                <defs>
                  <linearGradient id="modelGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#111" />
                    <stop offset="100%" stopColor="#333" />
                  </linearGradient>
                </defs>
                <g transform="translate(200, 150)">
                  {/* Left nodes */}
                  <circle cx="-120" cy="-80" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  <circle cx="-120" cy="-40" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  <circle cx="-120" cy="0" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  <circle cx="-120" cy="40" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  <circle cx="-120" cy="80" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  
                  {/* Middle nodes */}
                  <circle cx="0" cy="-60" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  <circle cx="0" cy="-20" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  <circle cx="0" cy="20" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  <circle cx="0" cy="60" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  
                  {/* Right nodes */}
                  <circle cx="120" cy="0" r="8" fill="#222" stroke="white" strokeWidth="1" />
                  
                  {/* Connection lines */}
                  {/* Draw connections from left to middle */}
                  <path d="M-120,-80 L0,-60" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,-80 L0,-20" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,-40 L0,-60" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,-40 L0,-20" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,-40 L0,20" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,0 L0,-20" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,0 L0,20" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,40 L0,20" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,40 L0,60" stroke="white" strokeWidth="0.5" />
                  <path d="M-120,80 L0,60" stroke="white" strokeWidth="0.5" />
                  
                  {/* Draw connections from middle to right */}
                  <path d="M0,-60 L120,0" stroke="white" strokeWidth="0.5" />
                  <path d="M0,-20 L120,0" stroke="white" strokeWidth="0.5" />
                  <path d="M0,20 L120,0" stroke="white" strokeWidth="0.5" />
                  <path d="M0,60 L120,0" stroke="white" strokeWidth="0.5" />
                  
                  {/* Highlight active nodes */}
                  <circle cx="-120" cy="-40" r="12" fill="none" stroke="#0066FF" strokeWidth="1.5" />
                  <circle cx="0" cy="20" r="12" fill="none" stroke="#0066FF" strokeWidth="1.5" />
                  <circle cx="120" cy="0" r="12" fill="none" stroke="#0066FF" strokeWidth="1.5" />
                  
                  {/* Highlight active connections */}
                  <path d="M-120,-40 L0,20" stroke="#0066FF" strokeWidth="1.5" />
                  <path d="M0,20 L120,0" stroke="#0066FF" strokeWidth="1.5" />
                </g>
                  </svg>
            </div>
            
            {/* Content */}
            <div style={{ flex: "1" }}>
              <div style={{ 
                borderLeft: "4px solid white",
                paddingLeft: "30px" 
              }}>
                <h3 style={{ 
                  fontSize: "2rem", 
                  fontWeight: "bold", 
                  color: "white", 
                  marginBottom: "20px" 
                }}>
                  [Geo OS Model]
                </h3>
                <p style={{ 
                  fontSize: "1.2rem", 
                  color: "rgba(255, 255, 255, 0.9)", 
                  lineHeight: "1.6", 
                  marginBottom: "30px" 
                }}>
                  Powers intelligent decision-making through advanced AI models specifically designed and optimized for geospatial analysis.
                </p>
                <ul style={{ 
                  listStyle: "none", 
                  padding: 0, 
                  margin: 0
                }}>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Earth Foundation Models
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      Pre-trained large-scale models that understand Earth's physical dynamics at global scale.
                    </p>
                  </li>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Predictive Analytics
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      Forecasts environmental conditions and operational outcomes with superhuman accuracy.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Geo OS Functions Section */}
          <div style={{ 
            display: "flex", 
            alignItems: "center",
            marginBottom: "120px",
            gap: "80px"
          }}>
            {/* Functions Visualization */}
            <div style={{ flex: "0 0 40%" }}>
              <svg width="100%" height="300" viewBox="0 0 400 300">
                <defs>
                  <linearGradient id="functionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#111" />
                    <stop offset="100%" stopColor="#333" />
                  </linearGradient>
                </defs>
                <g transform="translate(200, 150)">
                  {/* Function Modules */}
                  <g transform="translate(-65, -40)">
                    <rect width="50" height="50" fill="url(#functionGradient)" stroke="white" strokeWidth="1.5" rx="5" />
                    <path d="M10,25 L40,25 M25,10 L25,40" stroke="#0066FF" strokeWidth="2" />
                  </g>
                  
                  <g transform="translate(65, -40)">
                    <rect width="50" height="50" fill="url(#functionGradient)" stroke="white" strokeWidth="1.5" rx="5" />
                    <path d="M10,25 L40,25 M25,10 L25,40" stroke="#0066FF" strokeWidth="2" />
                  </g>
                  
                  <g transform="translate(0, 45)">
                    <rect width="50" height="50" fill="url(#functionGradient)" stroke="white" strokeWidth="1.5" rx="5" />
                    <path d="M10,25 L40,25 M25,10 L25,40" stroke="#0066FF" strokeWidth="2" />
                  </g>
                  
                  {/* Connecting Lines */}
                  <path d="M-40,-15 L0,45" stroke="#0066FF" strokeWidth="1.5" fill="none" />
                  <path d="M90,-15 L25,45" stroke="#0066FF" strokeWidth="1.5" fill="none" />
                </g>
              </svg>
            </div>
            
            {/* Content */}
            <div style={{ flex: "1" }}>
              <div style={{ 
                borderLeft: "4px solid white",
                paddingLeft: "30px" 
              }}>
                <h3 style={{ 
                  fontSize: "2rem", 
                  fontWeight: "bold", 
                  color: "white", 
                  marginBottom: "20px" 
                }}>
                  [Geo OS Functions]
                </h3>
                <p style={{ 
                  fontSize: "1.2rem", 
                  color: "rgba(255, 255, 255, 0.9)", 
                  lineHeight: "1.6", 
                  marginBottom: "30px" 
                }}>
                  Modular, composable functions for building sophisticated Earth operations workflows easily accessible via APIs.
                </p>
                <ul style={{ 
                  listStyle: "none", 
                  padding: 0, 
                  margin: 0
                }}>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Function Marketplace
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      Extensive library of pre-built functions for common Earth operations tasks.
                    </p>
                  </li>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Custom Functions
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      Build and deploy custom functions for specialized operational needs.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Geo OS SDK Section - Reversed */}
          <div style={{ 
            display: "flex", 
            flexDirection: "row-reverse",
            alignItems: "center",
            marginBottom: "120px",
            gap: "80px"
          }}>
            {/* SDK Visualization */}
            <div style={{ flex: "0 0 40%" }}>
              <svg width="100%" height="300" viewBox="0 0 400 300">
                <defs>
                  <linearGradient id="sdkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#111" />
                    <stop offset="100%" stopColor="#333" />
                  </linearGradient>
                </defs>
                <g transform="translate(200, 150)">
                  {/* Code Block Visual */}
                  <rect x="-100" y="-80" width="200" height="160" fill="url(#sdkGradient)" stroke="white" strokeWidth="1.5" rx="5" />
                  
                  {/* Code Lines */}
                  <line x1="-80" y1="-60" x2="40" y2="-60" stroke="#0066FF" strokeWidth="2" />
                  <line x1="-80" y1="-30" x2="60" y2="-30" stroke="white" strokeWidth="1" />
                  <line x1="-80" y1="0" x2="20" y2="0" stroke="white" strokeWidth="1" />
                  <line x1="-80" y1="30" x2="40" y2="30" stroke="#0066FF" strokeWidth="2" />
                  
                  {/* API Endpoints */}
                  <circle cx="-120" cy="-40" r="10" fill="#0066FF" stroke="white" strokeWidth="1" />
                  <circle cx="120" cy="-40" r="10" fill="#0066FF" stroke="white" strokeWidth="1" />
                  <path d="M-120,-40 C-80,-40 80,-40 120,-40" stroke="#0066FF" strokeWidth="1.5" fill="none" />
                </g>
              </svg>
            </div>

            {/* Content */}
            <div style={{ flex: "1" }}>
              <div style={{ 
                borderLeft: "4px solid white",
                paddingLeft: "30px" 
              }}>
                <h3 style={{ 
                  fontSize: "2rem", 
                  fontWeight: "bold", 
                  color: "white", 
                  marginBottom: "20px" 
                }}>
                  [Geo OS SDK]
                </h3>
                <p style={{ 
                  fontSize: "1.2rem", 
                  color: "rgba(255, 255, 255, 0.9)", 
                  lineHeight: "1.6", 
                  marginBottom: "30px" 
                }}>
                  Comprehensive development toolkit for building and deploying Earth-scale applications.
                </p>
                <ul style={{ 
                  listStyle: "none", 
                  padding: 0, 
                  margin: 0
                }}>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Developer Tools
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      Rich set of APIs, libraries, and tools for rapid application development.
                    </p>
                  </li>
                  <li style={{ marginBottom: "20px" }}>
                    <h4 style={{ 
                      fontSize: "1.3rem", 
                      color: "white", 
                      marginBottom: "10px" 
                    }}>
                      Deployment Framework
                    </h4>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "rgba(255, 255, 255, 0.8)", 
                      lineHeight: "1.5" 
                    }}>
                      Streamlined deployment and scaling of Earth-scale applications.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Section */}
        <CTASection>
            <Title style={{ fontSize: "2.5rem", marginBottom: "30px" }}>
              Ready to build with Geo OS?
            </Title>
          <CTAButton href="/partner-program">
              Learn more about Partner Program
          </CTAButton>
        </CTASection>
        </div>

        <Footer />
      </ContentContainer>
    </PageContainer>
  );
}

export default GeoOS; 